import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';

export type PaginationChange = {
    page: number;
    pageSize: number;
};

@Component({
    selector: 'app-pagination',
    templateUrl: './pagination.component.html',
    styleUrl: './pagination.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class PaginationComponent {
    @Input() public currentPage = 1;
    @Input() public pageSize = 200;
    @Input() public total = 0;
    @Input() public disabled = false;

    @Output() public pageChange = new EventEmitter<PaginationChange>();

    public readonly pageSizeOptions = [100, 200, 500];
    public readonly selectAllValue = -1;

    public get totalPages(): number {
        if (this.pageSize === this.selectAllValue) {
            return 1; // All items on one page
        }
        return Math.ceil(this.total / this.pageSize);
    }

    public get hasNextPage(): boolean {
        if (this.pageSize === this.selectAllValue) {
            return false; // No next page when showing all
        }
        return this.currentPage < this.totalPages;
    }

    public get hasPreviousPage(): boolean {
        return this.currentPage > 1;
    }

    public get startItem(): number {
        if (this.pageSize === this.selectAllValue) {
            return this.total > 0 ? 1 : 0;
        }
        return (this.currentPage - 1) * this.pageSize + 1;
    }

    public get endItem(): number {
        if (this.pageSize === this.selectAllValue) {
            return this.total;
        }
        return Math.min(this.currentPage * this.pageSize, this.total);
    }

    public onPreviousPage(): void {
        if (this.hasPreviousPage && !this.disabled) {
            this.pageChange.emit({
                page: this.currentPage - 1,
                pageSize: this.pageSize
            });
        }
    }

    public onNextPage(): void {
        if (this.hasNextPage && !this.disabled) {
            this.pageChange.emit({
                page: this.currentPage + 1,
                pageSize: this.pageSize
            });
        }
    }

    public onPageSizeChange(newPageSize: number): void {
        if (!this.disabled) {
            this.pageChange.emit({
                page: 1,
                pageSize: newPageSize
            });
        }
    }
}
